import React from 'react';

import classNames from 'classnames';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { useCommunityInfoContext } from 'components/CommunityInfo/CommunityInfoContext';
import BrochureAndDocsContent from 'components/CommunityInfo/general/documents/BrochureAndDocsContent';
import { FEES_PRICING_TYPES } from 'constants/careTypes';
import { isManagedBySuperuser } from 'helpers/permissions';
import { SnapshotDataType } from 'types/snapshot';
import { isNonNegativeNumber } from 'utils/helpers/numbers';

import SnapshotActor from '../../../../common/Snapshot/SnapshotActor';
import { useAppSelector } from '../../../../store/hook';
import {
  DOCUMENTS_FIELD,
  FEES_FIELD_COMMUNITY_FEE,
  FEES_FIELD_COMMUNITY_FEE_EQUIVALENT,
  FEES_FIELDS_LEVELS,
  FIELD_CATEGORY,
  LEVEL_FEES_FIELDS,
  SHOP_NOTES_FIELD,
} from '../../../ShopData/constants';
import { getLevelsAverage } from '../../careTypes/utils';
import ShopNotesContent from '../../general/shopNotes/ShopNotesContent';

const Container = styled.div`
  padding: 0px 30px 0px 30px;
  margin-bottom: 15px;
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const Info = styled.div`
  display: flex;
  white-space: nowrap;
  gap: 5px;
`;

const Amount = styled.div`
  display: flex;
`;

const LevelEntry = styled.div`
  display: flex;
  justify-content: center;
  width: 60px;
`;

const Date = styled.div`
  display: flex;
  font-family: var(--font-bold);
  font-size: 14px;
  color: var(--text-primary);
  gap: 5px;
`;

const Source = styled.div`
  display: flex;
  align-items: center;
  font-family: var(--font-regular);
  font-size: 12px;
  color: var(--text-secondary);
`;

const Value = styled.div`
  display: flex;
  font-size: 14px;
  color: var(--positive-green);
  &.small-font {
    font-size: 12px;
  }
`;

const ShopNotes = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  padding-top: 10px;
  padding-bottom: 6px;
  width: 730px;
`;

const Documents = styled.div`
  padding-bottom: 6px;
  width: 730px;
`;

interface Props {
  snapshot: SnapshotDataType | undefined;
  data?: any;
  levelFields: string[];
}

const HistoryDataModalSnapshotContent = ({ snapshot, data, levelFields }: Props) => {
  const { currentUser } = useAppSelector((state) => state.auth);
  const { history } = useCommunityInfoContext();

  if (!snapshot) return null;
  // Different date formats for admin (supporting the user) and non-admin
  const dateFormat = isManagedBySuperuser(currentUser) ? 'MMM DD YYYY' : 'MMM YYYY';

  // Check if the field is a community fee and adjust necessary values calculations
  const isCommunityFeeEquivalent = history?.field === FEES_FIELD_COMMUNITY_FEE && data.frequency;
  const dataField = isCommunityFeeEquivalent ? FEES_FIELD_COMMUNITY_FEE_EQUIVALENT : (history?.field ?? '');

  // Check if the field is a care fee and adjust necessary values calculations
  const isAverageCareFee =
    history?.category === FIELD_CATEGORY.FEES && history?.field === LEVEL_FEES_FIELDS.AVERAGE_COST;
  const isLevels = history?.category === FIELD_CATEGORY.FEES && history?.field === FEES_FIELDS_LEVELS;
  const isCareFee = isAverageCareFee || isLevels;

  const averageLevelsCareCost = isCareFee ? getLevelsAverage(data) : '';
  const averageCareCost = isNonNegativeNumber(data[LEVEL_FEES_FIELDS.AVERAGE_COST])
    ? data[LEVEL_FEES_FIELDS.AVERAGE_COST]
    : averageLevelsCareCost;

  const isAveragedCareFeeType = [
    FEES_PRICING_TYPES.LEVELS,
    FEES_PRICING_TYPES.A_LA_CARTE,
    FEES_PRICING_TYPES.POINTS,
  ].includes(data[LEVEL_FEES_FIELDS.FEES_PRICING_TYPE]);
  const isLevelsCareFeeType = data[LEVEL_FEES_FIELDS.FEES_PRICING_TYPE] === FEES_PRICING_TYPES.LEVELS;

  const isShopNotes = history?.field === SHOP_NOTES_FIELD;
  const isDocuments = history?.field === DOCUMENTS_FIELD;
  return (
    <Container>
      <FlexContainer>
        <Info>
          <Date>
            <span>{dayjs(snapshot?.date).format(dateFormat)}</span>
            {snapshot?.latest && <span>(Current Data)</span>}
          </Date>
          <Source>
            <SnapshotActor snapshot={snapshot} showTooltip />
          </Source>
        </Info>
        <Amount>
          {isCareFee && (
            <>
              <LevelEntry>
                <Value className={classNames({ 'small-font': Boolean(levelFields.length) })}>
                  {isNonNegativeNumber(averageCareCost) && isAveragedCareFeeType
                    ? `$${(averageCareCost as number).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                    : ''}
                </Value>
              </LevelEntry>
              {levelFields?.map((field: string) => (
                <LevelEntry key={field}>
                  <Value className={classNames('small-font')}>
                    {isNonNegativeNumber(data[field]) && isLevelsCareFeeType
                      ? `$${(data[field] as number).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                      : ''}
                  </Value>
                </LevelEntry>
              ))}
            </>
          )}
          {!isCareFee && isNonNegativeNumber(data[dataField]) && (
            <Value>${data[dataField].toLocaleString(undefined, { maximumFractionDigits: 0 })}</Value>
          )}
        </Amount>
      </FlexContainer>
      {isShopNotes && (
        <ShopNotes>
          <ShopNotesContent data={data} />
        </ShopNotes>
      )}
      {isDocuments && (
        <Documents>
          <BrochureAndDocsContent data={data} />
        </Documents>
      )}
    </Container>
  );
};

export default HistoryDataModalSnapshotContent;
